import {
  Autocomplete,
  Box,
  Button,
  Collapse,
  FormControl,
  FormHelperText,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../context/CurrencyContext";
import { Damage, Quantum } from "../shared/CaseModel";
import randomId from "../shared/randomId";
import { CurrencyTextField } from "./general/CurrencyTextField";
import { DialogSection, DialogShell } from "./general/DialogShell";
import { WeightedAwardsEditor } from "./WeightedAwardsEditor";

/** A damage of a claimant, with the claims that claim it. */
export type ClaimedDamage = {
  damage: Damage;
  claimedIn: { claimId: string; label: string }[];
};

export interface QuantumEditorProps {
  // the amount edited, with its damage and the other claims claiming that
  // damage; undefined for a new amount
  edited?: { quantum: Quantum } & ClaimedDamage;
  // the claimant, whose damage a new one is
  claimantId: string;
  // the claimant's damages the claim does not claim: picking one, or typing
  // its name, claims the same damage here, awarded once
  offeredDamages: ClaimedDamage[];
  // the names of the other damages the claim claims: none twice
  takenNames: string[];
  open: boolean;
  // with the damage to save along, where it is new or renamed
  onClose: (result?: { quantum: Quantum; damage?: Damage }) => void;
}

/** Room for an award with its slider and its weight beside it, in px. */
const WIDTH = 600;

const labels = ({ claimedIn }: ClaimedDamage) =>
  claimedIn.map(({ label }) => label).join(", ");

export const QuantumEditorDialog = ({
  edited,
  claimantId,
  offeredDamages,
  takenNames,
  open,
  onClose,
}: QuantumEditorProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.claims.quantums.editDialog",
  });
  const { t: tAssets } = useTranslation(undefined, {
    keyPrefix: "case.parties.assets.editDialog",
  });
  const { currency } = useCurrency();
  const [name, setName] = useState(edited?.damage.name);
  const [value, setValue] = useState(edited?.quantum.value);

  const [awardedShares, setAwardedShares] = useState(
    edited?.quantum.awardedShares || [],
  );

  const trimmed = name?.trim() ?? "";
  const isNameEmpty = trimmed.length === 0;
  // a damage the claim claims already cannot be claimed again
  const isNameForbidden = takenNames.includes(trimmed);
  // the name of a damage the claimant claims elsewhere: a new amount claims
  // that damage, a renamed one cannot take its name
  const offered = offeredDamages.find(({ damage }) => damage.name === trimmed);
  const isNameOfOther = edited !== undefined && offered !== undefined;

  const isValueEmpty = value === undefined || Number.isNaN(value);
  const isValueNegative = value !== undefined && value < 0;

  const submit = () => {
    if (value === undefined) {
      onClose();
      return;
    }
    if (edited !== undefined) {
      const { quantum, damage } = edited;
      onClose({
        quantum: { ...quantum, value, awardedShares },
        damage:
          trimmed === damage.name ? undefined : { ...damage, name: trimmed },
      });
      return;
    }
    // the damage claimed: one the claimant claims elsewhere, or a new one of
    // this name
    const damage = offered?.damage ?? {
      id: randomId(),
      claimantId,
      name: trimmed,
    };
    onClose({
      quantum: { damageId: damage.id, value, awardedShares },
      damage: offered ? undefined : damage,
    });
  };

  const nameError = isNameEmpty || isNameForbidden || isNameOfOther;
  const nameField = (params: React.ComponentProps<typeof TextField> = {}) => (
    <TextField
      {...params}
      label={t("sections.name.label.name")}
      error={nameError}
    />
  );

  return (
    <DialogShell
      open={open}
      onClose={() => onClose()}
      title={t("title")}
      width={WIDTH}
      sectioned
      actions={
        <>
          <Button onClick={() => onClose()} color="inherit">
            {t("cancel")}
          </Button>
          <Button
            disabled={
              nameError ||
              isValueEmpty ||
              isValueNegative ||
              awardedShares.length < 1
            }
            onClick={submit}
            variant="contained"
            autoFocus
          >
            {t("submit")}
          </Button>
        </>
      }
    >
      <DialogSection
        first
        label={t("sections.name.title")}
        description={t("sections.name.description")}
      >
        <FormControl
          key={"claimant-selection"}
          fullWidth
          required
          error={nameError}
        >
          {edited !== undefined ? (
            // the name is the damage's: changing it renames the damage
            nameField({
              size: "small",
              value: name ?? "",
              onChange: (event) => setName(event.target.value),
            })
          ) : (
            <Autocomplete
              freeSolo
              size="small"
              options={offeredDamages.map(({ damage }) => damage.name)}
              renderOption={({ key, ...props }, option) => {
                const claimed = offeredDamages.find(
                  ({ damage }) => damage.name === option,
                );
                return (
                  <Box component="li" key={key} {...props}>
                    <Box>
                      <Typography variant="body2">{option}</Typography>
                      {claimed && (
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          {t("sections.name.claimedIn", {
                            claims: labels(claimed),
                          })}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              }}
              renderInput={(params) => nameField(params)}
              onInputChange={(_, input) => setName(input)}
            />
          )}
          {edited === undefined && offered && (
            <FormHelperText>
              {t("sections.name.helperText.sameDamage", {
                claims: labels(offered),
              })}
            </FormHelperText>
          )}
          {edited !== undefined &&
            edited.claimedIn.length > 0 &&
            !nameError &&
            trimmed !== edited.damage.name && (
              <FormHelperText>
                {t("sections.name.helperText.renamesShared", {
                  claims: labels(edited),
                })}
              </FormHelperText>
            )}
          {isNameEmpty && (
            <FormHelperText>
              {t("sections.name.helperText.emptyName")}
            </FormHelperText>
          )}
          {isNameForbidden && (
            <FormHelperText>
              {t("sections.name.helperText.nameAlreadyUsed")}
            </FormHelperText>
          )}
          {edited !== undefined && offered && (
            <FormHelperText>
              {t("sections.name.helperText.nameOfOtherDamage", {
                claims: labels(offered),
              })}
            </FormHelperText>
          )}
        </FormControl>
      </DialogSection>
      <DialogSection
        label={t("sections.value.title")}
        description={t("sections.value.description")}
      >
        <FormControl
          key={"value-section"}
          required
          error={isValueEmpty || isValueNegative}
          sx={{ maxWidth: 240 }}
        >
          <CurrencyTextField
            currency={currency}
            label={t("sections.value.label.value")}
            onValueChange={setValue}
            required
            error={isValueEmpty || isValueNegative}
            variant="outlined"
            size="small"
            min={0}
            value={value}
          />
          {isValueEmpty && (
            <FormHelperText>
              {t("sections.value.helperText.valueEmpty")}
            </FormHelperText>
          )}
          {isValueNegative && (
            <FormHelperText>
              {t("sections.value.helperText.valueNegative")}
            </FormHelperText>
          )}
        </FormControl>
      </DialogSection>
      {/* an award is a share of the value, so it waits for one */}
      <Collapse in={value !== undefined && !Number.isNaN(value) && value !== 0}>
        <DialogSection
          label={t("sections.award.title")}
          description={t("sections.award.description")}
        >
          <div>
            <WeightedAwardsEditor
              values={awardedShares}
              // pass value update
              onChange={setAwardedShares}
              currency={currency}
              maxAward={value || 0}
            />
            {awardedShares.length < 1 && (
              <FormHelperText error>
                {tAssets("helper.valueMissing")}
              </FormHelperText>
            )}
          </div>
        </DialogSection>
      </Collapse>
    </DialogShell>
  );
};
